/**
 * Lightweight i18n for Attestto Verify
 *
 * Usage:
 *   import { t, currentLang, setLang } from '../i18n.js'
 *   t('sign.title')  // => "Sign a Document"
 *
 * The language is stored in localStorage and emits 'attestto-lang-change'
 * on `window` so both Lit components and vanilla HTML can react.
 */

export type Lang = 'en' | 'es'

const translations: Record<Lang, Record<string, string>> = {
  en: {
    // ── Nav ──────────────────────────────────────────────
    'nav.verify': 'Verify',
    'nav.sign': 'Sign',
    'nav.developers': 'Developers',

    // ── Verify page (index.html) ─────────────────────────
    'verify.title': 'Document Verification',
    'verify.subtitle': 'Verify integrity and signatures — your file never leaves your device.',
    'verify.subtitle.shared': 'Someone shared a verified document — drop your copy to confirm it matches.',
    'verify.audit.title': 'Technical Audit',

    // Verify footer
    'verify.footer.whatGetsVerified': 'What gets verified',
    'verify.footer.item1': 'Document integrity (SHA-256, ByteRange)',
    'verify.footer.item2': 'Digital signatures (PAdES, CAdES, PKCS#7)',
    'verify.footer.item3': 'PKI recognition (CR Firma Digital)',
    'verify.footer.item4': 'Security scan (JS, auto-actions, embeds)',
    'verify.footer.item5': 'Metadata analysis',
    'verify.footer.levels': 'Verification Levels',
    'verify.footer.detected': 'Signature found',
    'verify.footer.parsed': 'Cert chain extracted',
    'verify.footer.verified': 'Crypto proof valid',
    'verify.footer.privacy': 'Privacy',
    'verify.footer.privacyText': 'Your file never leaves your device. All verification runs 100% in the browser — no upload, no backend, no tracking.',
    'verify.footer.viewSource': 'View source',
    'verify.footer.signatureTypes': 'Signature Types',
    'verify.footer.recognizedPki': 'Recognized PKI',
    'verify.footer.capabilities': 'Capabilities',
    'verify.footer.zeroUpload': 'Zero upload',
    'verify.footer.clientSide': 'Client-side',
    'verify.footer.openSource': 'Open source',
    'verify.footer.browsers': 'Browsers',
    'verify.footer.networkTransparency': 'Network Transparency',
    'verify.footer.networkText': 'All verification runs 100% in your browser. No document data is ever transmitted to any server. Network requests visible in DevTools (e.g. Cloudflare CDN, GitHub Pages) serve only the application code — never your files.',
    'verify.footer.verifySource': 'Verify in the source code',
    'verify.footer.license': 'Open source under Apache 2.0',
    'verify.footer.fileNeverLeaves': 'Your file never leaves your device.',

    // Audit panel
    'audit.signed': 'Signed',
    'audit.unsigned': 'Unsigned',
    'audit.editable': 'Editable',
    'audit.locked': 'Locked',
    'audit.canSign': 'Can sign',
    'audit.safe': 'Safe',
    'audit.hasScripts': 'Has scripts',
    'audit.longTermValid': 'Long-term valid',
    'audit.webOptimized': 'Web-optimized',
    'audit.documentHash': 'Document Hash',
    'audit.documentProperties': 'Document Properties',
    'audit.pdfVersion': 'PDF Version',
    'audit.pages': 'Pages',
    'audit.linearized': 'Linearized',
    'audit.encryption': 'Encryption',
    'audit.securityScan': 'Security Scan',
    'audit.javascript': 'JavaScript',
    'audit.scripts': 'script(s)',
    'audit.noneSafe': 'None (safe)',
    'audit.autoActions': 'Auto Actions',
    'audit.detected': 'Detected',
    'audit.embeddedFiles': 'Embedded Files',
    'audit.files': 'file(s)',
    'audit.none': 'None',
    'audit.externalLinks': 'External Links',
    'audit.uris': 'URI(s)',
    'audit.signatureIntegrity': 'Signature Integrity',
    'audit.sig': 'Sig',
    'audit.ltvData': 'LTV Data',
    'audit.ltvPresent': 'Present (offline revocation)',
    'audit.ltvMissing': 'Not embedded (requires online check)',
    'audit.yes': 'Yes',
    'audit.no': 'No',
    'audit.unknown': 'Unknown',

    // ── Sign page (sign/index.html) ──────────────────────
    'sign.title': 'Sign a Document',
    'sign.subtitle': 'Sign with your DID wallet or browser key — your file never leaves your device.',
    'sign.howItWorks': 'How it works',
    'sign.step1.title': 'Connect',
    'sign.step1.desc': 'DID wallet auto-detected, or generate a browser key.',
    'sign.step2.title': 'Drop a PDF',
    'sign.step2.desc': 'File stays on your device. SHA-256 hash computed locally.',
    'sign.step3.title': 'Sign',
    'sign.step3.desc': 'W3C Verifiable Credential created and stored in your wallet.',
    'sign.method.didWallet': 'DID Wallet',
    'sign.method.recommended': 'Recommended',
    'sign.method.didDesc': 'Connect Attestto ID or any CHAPI-compatible wallet.',
    'sign.method.browserKey': 'Browser Key',
    'sign.method.instant': 'Instant',
    'sign.method.browserKeyDesc': 'Self-issued did:key in your browser. Works immediately.',
    'sign.method.firmaDigital': 'Firma Digital',
    'sign.method.comingSoon': 'Coming soon',
    'sign.method.firmaDigitalDesc': 'Costa Rica Firma Digital smart card. PKI-backed.',
    'sign.method.verifyAfter': 'Verify after signing',
    'sign.method.verifyAt': 'Verify at',
    'sign.footer.networkTransparency': 'Network Transparency',
    'sign.footer.networkText': 'All signing runs 100% in your browser. No document data is ever transmitted to any server. Network requests visible in DevTools (e.g. Cloudflare CDN, GitHub Pages) serve only the application code — never your files.',
    'sign.footer.license': 'Open source under Apache 2.0',
    'sign.footer.fileNeverLeaves': 'Your file never leaves your device.',

    // ── attestto-verify component ────────────────────────
    'comp.verify.hashCopied': 'Hash copied to clipboard',
    'comp.verify.loading.hint': 'All processing happens locally — your file never leaves this device',
    'comp.verify.dropFile': 'Drop file here',
    'comp.verify.dropShared': 'Someone shared a verified document — drop your copy to confirm',
    'comp.verify.dropVerify': 'Drop a document to verify',
    'comp.verify.dropHintShared': "We'll compare your file's hash against the shared verification",
    'comp.verify.dropHint': 'PDF, Word, or any file — never leaves your device',
    'comp.verify.metadata': 'Document Metadata',
    'comp.verify.title': 'Title',
    'comp.verify.author': 'Author',
    'comp.verify.subject': 'Subject',
    'comp.verify.creator': 'Creator',
    'comp.verify.producer': 'Producer',
    'comp.verify.created': 'Created',
    'comp.verify.modified': 'Modified',
    'comp.verify.digitalSigs': 'Digital Signatures',
    'comp.verify.noSigs': 'No digital signatures found',
    'comp.verify.organization': 'Organization',
    'comp.verify.trustPermissions': 'Trust Permissions',
    'comp.verify.certChain': 'Certificate Chain',
    'comp.verify.reason': 'Reason',
    'comp.verify.location': 'Location',
    'comp.verify.contact': 'Contact',
    'comp.verify.signed': 'Signed',
    'comp.verify.extVerification': 'Extended Verification',
    'comp.verify.valid': 'Valid',
    'comp.verify.failed': 'Failed',
    'comp.verify.shareLinkCopied': 'Link copied!',
    'comp.verify.shareLink': 'Share verification link',
    'comp.verify.verifyAnother': 'Verify another document',
    'comp.verify.shareHint': 'Share the link so others can verify they have the same document',
    'comp.verify.docMatches': 'Document matches',
    'comp.verify.docNoMatch': 'Document does not match',
    'comp.verify.matchDetail': 'Your copy is identical to the one that was verified.',
    'comp.verify.noMatchDetail': 'The hash of your file differs from the shared verification. This may be a different version or a modified copy.',
    'comp.verify.signYourOwn': 'Sign your own documents',
    'comp.verify.readingFile': 'Reading file...',
    'comp.verify.computingHash': 'Computing SHA-256 hash...',
    'comp.verify.loadingPdf': 'Loading PDF engine...',
    'comp.verify.extractingMeta': 'Extracting metadata...',
    'comp.verify.shareTitle': 'Document Verification — Attestto',
    'comp.verify.shareText': 'I verified a document. Drop your copy to confirm it matches.',
    'comp.verify.decentralizedId': 'Decentralized Identifier',
    'comp.verify.nationalId': 'National ID',
    'comp.verify.protectIdentity': 'Protect your own identity',
    'comp.verify.getAttesttoId': 'Get Attestto ID',
    'comp.verify.proveRelationship': 'To view the signer\'s identity, prove your relationship:',
    'comp.verify.iAmSigner': 'I am the signer',
    'comp.verify.verifyEmail': 'Enter your email to verify',
    'comp.verify.iKnowSigner': 'I know the signer',
    'comp.verify.enterNationalId': 'Enter the full national ID to confirm',
    'comp.verify.cancel': 'Cancel',
    'comp.verify.enterEmailPrompt': 'Enter the email address associated with this signature:',
    'comp.verify.verify': 'Verify',
    'comp.verify.back': 'Back',
    'comp.verify.enterIdPrompt': 'Enter the full national ID to confirm you already have this information:',
    'comp.verify.fullNationalId': 'Full national ID',
    'comp.verify.confirm': 'Confirm',
    'comp.verify.reveal': 'Reveal',
    'comp.verify.enterEmail': 'Please enter an email address',
    'comp.verify.emailNoMatch': 'Email does not match the signer certificate',
    'comp.verify.enterId': 'Please enter the full national ID',
    'comp.verify.idNoMatch': 'National ID does not match — check the format (e.g. CPF-0123456789)',
    'comp.verify.filename': 'Filename',

    // Badge labels
    'comp.verify.badge.detected': 'Detected',
    'comp.verify.badge.parsed': 'Parsed',
    'comp.verify.badge.signed': 'Verified',
    'comp.verify.badge.trusted': 'Trusted',
    'comp.verify.badge.qualified': 'Qualified',
    'comp.verify.badge.unknown': 'Unknown',
    'comp.verify.badge.none': 'None',

    // Level hints
    'comp.verify.hint.detected': 'Signature structure found — cryptographic verification pending (v2)',
    'comp.verify.hint.parsed': 'Certificate chain extracted — cryptographic verification pending (v2)',
    'comp.verify.hint.signed': 'Cryptographically valid — signature math verified',
    'comp.verify.hint.verified': 'Cryptographically valid — signature math verified and document content intact',
    'comp.verify.hint.tampered': 'Document content has been modified after signing — DO NOT TRUST',
    'comp.verify.hint.unknown': 'Integrity check could not be completed — neither valid nor tampered',
    'comp.verify.hint.trusted': 'Chain reaches a recognized Certificate Authority',
    'comp.verify.hint.qualified': 'Qualified corporate identity — GLEIF vLEI verified',
    // ATT-361 — Attestto self-attested signatures
    'comp.verify.attestto.title': 'Attestto self-attested signature',
    'comp.verify.attestto.verified': 'Cryptographically verified.',
    'comp.verify.attestto.verifiedBody': "The signer's ed25519 key proved possession of their Attestto vault, and the signed payload is byte-identical to the document hash. This is a Nivel B self-attested credential — trust is anchored in the signer's verified KYC, not in a third-party Certificate Authority.",
    'comp.verify.attestto.kycSource': 'KYC source',
    'comp.verify.attestto.padronCR': 'Padrón Electoral · Tribunal Supremo de Elecciones',
    'comp.verify.attestto.proofType': 'Proof type',
    'comp.verify.attestto.modeFinal': 'Document final · locked',
    'comp.verify.attestto.modeOpen': 'Open · counter-signatures allowed',
    'comp.verify.attestto.demoWarning': 'DEMO signature — not legally binding',

    // ── attestto-sign component ──────────────────────────
    'comp.sign.discovering': 'Discovering credential wallets...',
    'comp.sign.change': 'Change',
    'comp.sign.by': 'by',
    'comp.sign.walletsFound': 'wallet(s) found — Connect',
    'comp.sign.browserKey': 'Browser Key',
    'comp.sign.selfIssued': 'self-issued',
    'comp.sign.noWallet': 'No DID wallet found',
    'comp.sign.signBrowserKey': 'Sign with browser key',
    'comp.sign.retry': 'Retry',
    'comp.sign.dropDid': 'Drop a PDF to sign with your DID',
    'comp.sign.dropBrowserKey': 'Drop a PDF to sign with browser key',
    'comp.sign.dropSign': 'Drop a PDF to sign',
    'comp.sign.fileNeverLeaves': 'Your document never leaves your device',
    'comp.sign.onlyPdf': 'Only PDF files can be signed',
    'comp.sign.selectPdf': '1. Select PDF',
    'comp.sign.signStep': '2. Sign',
    'comp.sign.downloadStep': '3. Download',
    'comp.sign.signing': 'Signing...',
    'comp.sign.signingHint': 'All signing happens locally — your file never leaves this device',
    'comp.sign.computingHash': 'Computing hash...',
    'comp.sign.waitingWallet': 'Waiting for wallet approval...',
    'comp.sign.signingBrowserKey': 'Signing with browser key...',
    'comp.sign.signWith': 'Sign with',
    'comp.sign.signWithBrowserKey': 'Sign with browser key',
    'comp.sign.connectFirst': 'Connect wallet first',
    'comp.sign.signedBrowserKey': 'Signed with browser key',
    'comp.sign.signedWallet': 'Signed — credential stored in your wallet',
    'comp.sign.verifyDoc': 'Verify this document',
    'comp.sign.downloadPdf': 'Download Signed PDF',
    'comp.sign.exportJson': 'Export Credential (.json)',
    'comp.sign.signAnother': 'Sign another document',
    'comp.sign.cancelBtn': 'Cancel',
    'comp.sign.walletTimeout': 'Wallet did not respond in time. Click "Sign" to retry, or try "Sign with browser key" below.',
    'comp.sign.signingFailed': 'Signing failed',
    'comp.sign.useBrowserKeyInstead': 'Use browser key instead',
    'comp.sign.modalTitle': 'Document Signed & Downloaded',
    'comp.sign.modalVerify': 'Verify this document',
    'comp.sign.modalContinue': 'Continue signing',
  },
  es: {
    // ── Nav ──────────────────────────────────────────────
    'nav.verify': 'Verificar',
    'nav.sign': 'Firmar',
    'nav.developers': 'Desarrolladores',

    // ── Verify page (index.html) ─────────────────────────
    'verify.title': 'Verificación de Documentos',
    'verify.subtitle': 'Verifica la integridad y firmas — tu archivo nunca sale de tu dispositivo.',
    'verify.subtitle.shared': 'Alguien compartió un documento verificado — sube tu copia para confirmar que coincide.',
    'verify.audit.title': 'Auditoría Técnica',

    // Verify footer
    'verify.footer.whatGetsVerified': 'Qué se verifica',
    'verify.footer.item1': 'Integridad del documento (SHA-256, ByteRange)',
    'verify.footer.item2': 'Firmas digitales (PAdES, CAdES, PKCS#7)',
    'verify.footer.item3': 'Reconocimiento PKI (CR Firma Digital)',
    'verify.footer.item4': 'Escaneo de seguridad (JS, acciones automáticas, embebidos)',
    'verify.footer.item5': 'Análisis de metadatos',
    'verify.footer.levels': 'Niveles de Verificación',
    'verify.footer.detected': 'Firma encontrada',
    'verify.footer.parsed': 'Cadena de certificados extraída',
    'verify.footer.verified': 'Prueba criptográfica válida',
    'verify.footer.privacy': 'Privacidad',
    'verify.footer.privacyText': 'Tu archivo nunca sale de tu dispositivo. Toda la verificación se ejecuta 100% en el navegador — sin subidas, sin backend, sin rastreo.',
    'verify.footer.viewSource': 'Ver código fuente',
    'verify.footer.signatureTypes': 'Tipos de Firma',
    'verify.footer.recognizedPki': 'PKI Reconocida',
    'verify.footer.capabilities': 'Capacidades',
    'verify.footer.zeroUpload': 'Sin subidas',
    'verify.footer.clientSide': 'En el navegador',
    'verify.footer.openSource': 'Código abierto',
    'verify.footer.browsers': 'Navegadores',
    'verify.footer.networkTransparency': 'Transparencia de Red',
    'verify.footer.networkText': 'Toda la verificación se ejecuta 100% en tu navegador. Ningún dato del documento se transmite a ningún servidor. Las solicitudes de red visibles en DevTools (ej. Cloudflare CDN, GitHub Pages) solo sirven el código de la aplicación — nunca tus archivos.',
    'verify.footer.verifySource': 'Verificar en el código fuente',
    'verify.footer.license': 'Código abierto bajo Apache 2.0',
    'verify.footer.fileNeverLeaves': 'Tu archivo nunca sale de tu dispositivo.',

    // Audit panel
    'audit.signed': 'Firmado',
    'audit.unsigned': 'Sin firma',
    'audit.editable': 'Editable',
    'audit.locked': 'Bloqueado',
    'audit.canSign': 'Se puede firmar',
    'audit.safe': 'Seguro',
    'audit.hasScripts': 'Tiene scripts',
    'audit.longTermValid': 'Validez a largo plazo',
    'audit.webOptimized': 'Optimizado para web',
    'audit.documentHash': 'Hash del Documento',
    'audit.documentProperties': 'Propiedades del Documento',
    'audit.pdfVersion': 'Versión PDF',
    'audit.pages': 'Páginas',
    'audit.linearized': 'Linearizado',
    'audit.encryption': 'Cifrado',
    'audit.securityScan': 'Escaneo de Seguridad',
    'audit.javascript': 'JavaScript',
    'audit.scripts': 'script(s)',
    'audit.noneSafe': 'Ninguno (seguro)',
    'audit.autoActions': 'Acciones Automáticas',
    'audit.detected': 'Detectadas',
    'audit.embeddedFiles': 'Archivos Embebidos',
    'audit.files': 'archivo(s)',
    'audit.none': 'Ninguno',
    'audit.externalLinks': 'Enlaces Externos',
    'audit.uris': 'URI(s)',
    'audit.signatureIntegrity': 'Integridad de la Firma',
    'audit.sig': 'Firma',
    'audit.ltvData': 'Datos LTV',
    'audit.ltvPresent': 'Presente (revocación sin conexión)',
    'audit.ltvMissing': 'No embebido (requiere verificación en línea)',
    'audit.yes': 'Sí',
    'audit.no': 'No',
    'audit.unknown': 'Desconocido',

    // ── Sign page (sign/index.html) ──────────────────────
    'sign.title': 'Firmar un Documento',
    'sign.subtitle': 'Firma con tu billetera DID o clave del navegador — tu archivo nunca sale de tu dispositivo.',
    'sign.howItWorks': 'Cómo funciona',
    'sign.step1.title': 'Conectar',
    'sign.step1.desc': 'Billetera DID detectada automáticamente, o genera una clave del navegador.',
    'sign.step2.title': 'Sube un PDF',
    'sign.step2.desc': 'El archivo permanece en tu dispositivo. Hash SHA-256 calculado localmente.',
    'sign.step3.title': 'Firmar',
    'sign.step3.desc': 'Se crea una Credencial Verificable W3C y se almacena en tu billetera.',
    'sign.method.didWallet': 'Billetera DID',
    'sign.method.recommended': 'Recomendado',
    'sign.method.didDesc': 'Conecta Attestto ID o cualquier billetera compatible con CHAPI.',
    'sign.method.browserKey': 'Clave del Navegador',
    'sign.method.instant': 'Instantáneo',
    'sign.method.browserKeyDesc': 'did:key autoemitido en tu navegador. Funciona de inmediato.',
    'sign.method.firmaDigital': 'Firma Digital',
    'sign.method.comingSoon': 'Próximamente',
    'sign.method.firmaDigitalDesc': 'Tarjeta inteligente Firma Digital de Costa Rica. Respaldada por PKI.',
    'sign.method.verifyAfter': 'Verificar después de firmar',
    'sign.method.verifyAt': 'Verificar en',
    'sign.footer.networkTransparency': 'Transparencia de Red',
    'sign.footer.networkText': 'Toda la firma se ejecuta 100% en tu navegador. Ningún dato del documento se transmite a ningún servidor. Las solicitudes de red visibles en DevTools (ej. Cloudflare CDN, GitHub Pages) solo sirven el código de la aplicación — nunca tus archivos.',
    'sign.footer.license': 'Código abierto bajo Apache 2.0',
    'sign.footer.fileNeverLeaves': 'Tu archivo nunca sale de tu dispositivo.',

    // ── attestto-verify component ────────────────────────
    'comp.verify.hashCopied': 'Hash copiado al portapapeles',
    'comp.verify.loading.hint': 'Todo el procesamiento ocurre localmente — tu archivo nunca sale de este dispositivo',
    'comp.verify.dropFile': 'Suelta el archivo aquí',
    'comp.verify.dropShared': 'Alguien compartió un documento verificado — sube tu copia para confirmar',
    'comp.verify.dropVerify': 'Sube un documento para verificar',
    'comp.verify.dropHintShared': 'Compararemos el hash de tu archivo con la verificación compartida',
    'comp.verify.dropHint': 'PDF, Word o cualquier archivo — nunca sale de tu dispositivo',
    'comp.verify.metadata': 'Metadatos del Documento',
    'comp.verify.title': 'Título',
    'comp.verify.author': 'Autor',
    'comp.verify.subject': 'Asunto',
    'comp.verify.creator': 'Creador',
    'comp.verify.producer': 'Productor',
    'comp.verify.created': 'Creado',
    'comp.verify.modified': 'Modificado',
    'comp.verify.digitalSigs': 'Firmas Digitales',
    'comp.verify.noSigs': 'No se encontraron firmas digitales',
    'comp.verify.organization': 'Organización',
    'comp.verify.trustPermissions': 'Permisos de Confianza',
    'comp.verify.certChain': 'Cadena de Certificados',
    'comp.verify.reason': 'Razón',
    'comp.verify.location': 'Ubicación',
    'comp.verify.contact': 'Contacto',
    'comp.verify.signed': 'Firmado',
    'comp.verify.extVerification': 'Verificación Extendida',
    'comp.verify.valid': 'Válido',
    'comp.verify.failed': 'Fallido',
    'comp.verify.shareLinkCopied': '¡Enlace copiado!',
    'comp.verify.shareLink': 'Compartir enlace de verificación',
    'comp.verify.verifyAnother': 'Verificar otro documento',
    'comp.verify.shareHint': 'Comparte el enlace para que otros verifiquen que tienen el mismo documento',
    'comp.verify.docMatches': 'El documento coincide',
    'comp.verify.docNoMatch': 'El documento no coincide',
    'comp.verify.matchDetail': 'Tu copia es idéntica a la que fue verificada.',
    'comp.verify.noMatchDetail': 'El hash de tu archivo difiere de la verificación compartida. Puede ser una versión diferente o una copia modificada.',
    'comp.verify.signYourOwn': 'Firma tus propios documentos',
    'comp.verify.readingFile': 'Leyendo archivo...',
    'comp.verify.computingHash': 'Calculando hash SHA-256...',
    'comp.verify.loadingPdf': 'Cargando motor PDF...',
    'comp.verify.extractingMeta': 'Extrayendo metadatos...',
    'comp.verify.shareTitle': 'Verificación de Documento — Attestto',
    'comp.verify.shareText': 'Verifiqué un documento. Sube tu copia para confirmar que coincide.',
    'comp.verify.decentralizedId': 'Identificador Descentralizado',
    'comp.verify.nationalId': 'Identificación Nacional',
    'comp.verify.protectIdentity': 'Protege tu propia identidad',
    'comp.verify.getAttesttoId': 'Obtén Attestto ID',
    'comp.verify.proveRelationship': 'Para ver la identidad del firmante, demuestra tu relación:',
    'comp.verify.iAmSigner': 'Soy el firmante',
    'comp.verify.verifyEmail': 'Ingresa tu correo para verificar',
    'comp.verify.iKnowSigner': 'Conozco al firmante',
    'comp.verify.enterNationalId': 'Ingresa la identificación nacional completa para confirmar',
    'comp.verify.cancel': 'Cancelar',
    'comp.verify.enterEmailPrompt': 'Ingresa la dirección de correo asociada a esta firma:',
    'comp.verify.verify': 'Verificar',
    'comp.verify.back': 'Volver',
    'comp.verify.enterIdPrompt': 'Ingresa la identificación nacional completa para confirmar que ya tienes esta información:',
    'comp.verify.fullNationalId': 'Identificación nacional completa',
    'comp.verify.confirm': 'Confirmar',
    'comp.verify.reveal': 'Revelar',
    'comp.verify.enterEmail': 'Por favor ingresa una dirección de correo',
    'comp.verify.emailNoMatch': 'El correo no coincide con el certificado del firmante',
    'comp.verify.enterId': 'Por favor ingresa la identificación nacional completa',
    'comp.verify.idNoMatch': 'La identificación nacional no coincide — verifica el formato (ej. CPF-0123456789)',
    'comp.verify.filename': 'Nombre del archivo',

    // Badge labels
    'comp.verify.badge.detected': 'Detectada',
    'comp.verify.badge.parsed': 'Analizada',
    'comp.verify.badge.signed': 'Verificada',
    'comp.verify.badge.trusted': 'Confiable',
    'comp.verify.badge.qualified': 'Cualificada',
    'comp.verify.badge.unknown': 'Desconocida',
    'comp.verify.badge.none': 'Ninguna',

    // Level hints
    'comp.verify.hint.detected': 'Estructura de firma encontrada — verificación criptográfica pendiente (v2)',
    'comp.verify.hint.parsed': 'Cadena de certificados extraída — verificación criptográfica pendiente (v2)',
    'comp.verify.hint.signed': 'Criptográficamente válida — matemáticas de firma verificadas',
    'comp.verify.hint.verified': 'Criptográficamente válida — firma verificada y contenido del documento íntegro',
    'comp.verify.hint.tampered': 'El contenido del documento fue modificado después de firmarse — NO CONFIAR',
    'comp.verify.hint.unknown': 'No se pudo completar la verificación de integridad — ni válida ni manipulada',
    'comp.verify.hint.trusted': 'La cadena alcanza una Autoridad de Certificación reconocida',
    'comp.verify.hint.qualified': 'Identidad corporativa cualificada — vLEI de GLEIF verificada',
    // ATT-361 — Firmas Attestto auto-atestadas
    'comp.verify.attestto.title': 'Firma Attestto auto-atestada',
    'comp.verify.attestto.verified': 'Criptográficamente verificada.',
    'comp.verify.attestto.verifiedBody': 'La clave ed25519 del firmante demostró posesión de su bóveda Attestto, y el contenido firmado coincide byte a byte con el hash del documento. Esta es una credencial Nivel B auto-atestada — la confianza se ancla en el KYC verificado del firmante, no en una Autoridad de Certificación de terceros.',
    'comp.verify.attestto.kycSource': 'Fuente KYC',
    'comp.verify.attestto.padronCR': 'Padrón Electoral · Tribunal Supremo de Elecciones',
    'comp.verify.attestto.proofType': 'Tipo de prueba',
    'comp.verify.attestto.modeFinal': 'Documento final · bloqueado',
    'comp.verify.attestto.modeOpen': 'Abierto · permite contrafirmas',
    'comp.verify.attestto.demoWarning': 'Firma DEMO — no vinculante legalmente',

    // ── attestto-sign component ──────────────────────────
    'comp.sign.discovering': 'Descubriendo billeteras de credenciales...',
    'comp.sign.change': 'Cambiar',
    'comp.sign.by': 'por',
    'comp.sign.walletsFound': 'billetera(s) encontrada(s) — Conectar',
    'comp.sign.browserKey': 'Clave del Navegador',
    'comp.sign.selfIssued': 'autoemitido',
    'comp.sign.noWallet': 'No se encontró billetera DID',
    'comp.sign.signBrowserKey': 'Firmar con clave del navegador',
    'comp.sign.retry': 'Reintentar',
    'comp.sign.dropDid': 'Sube un PDF para firmar con tu DID',
    'comp.sign.dropBrowserKey': 'Sube un PDF para firmar con clave del navegador',
    'comp.sign.dropSign': 'Sube un PDF para firmar',
    'comp.sign.fileNeverLeaves': 'Tu documento nunca sale de tu dispositivo',
    'comp.sign.onlyPdf': 'Solo se pueden firmar archivos PDF',
    'comp.sign.selectPdf': '1. Seleccionar PDF',
    'comp.sign.signStep': '2. Firmar',
    'comp.sign.downloadStep': '3. Descargar',
    'comp.sign.signing': 'Firmando...',
    'comp.sign.signingHint': 'Toda la firma ocurre localmente — tu archivo nunca sale de este dispositivo',
    'comp.sign.computingHash': 'Calculando hash...',
    'comp.sign.waitingWallet': 'Esperando aprobación de la billetera...',
    'comp.sign.signingBrowserKey': 'Firmando con clave del navegador...',
    'comp.sign.signWith': 'Firmar con',
    'comp.sign.signWithBrowserKey': 'Firmar con clave del navegador',
    'comp.sign.connectFirst': 'Conectar billetera primero',
    'comp.sign.signedBrowserKey': 'Firmado con clave del navegador',
    'comp.sign.signedWallet': 'Firmado — credencial almacenada en tu billetera',
    'comp.sign.verifyDoc': 'Verificar este documento',
    'comp.sign.downloadPdf': 'Descargar PDF Firmado',
    'comp.sign.exportJson': 'Exportar Credencial (.json)',
    'comp.sign.signAnother': 'Firmar otro documento',
    'comp.sign.cancelBtn': 'Cancelar',
    'comp.sign.walletTimeout': 'La billetera no respondió a tiempo. Haz clic en "Firmar" para reintentar, o prueba "Firmar con clave del navegador" abajo.',
    'comp.sign.signingFailed': 'La firma falló',
    'comp.sign.useBrowserKeyInstead': 'Usar clave del navegador en su lugar',
    'comp.sign.modalTitle': 'Documento Firmado y Descargado',
    'comp.sign.modalVerify': 'Verificar este documento',
    'comp.sign.modalContinue': 'Continuar firmando',
  },
}

const STORAGE_KEY = 'attestto-lang'

let _lang: Lang = 'en'

/** Initialize language from localStorage */
function init() {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY) as Lang | null
    if (stored === 'en' || stored === 'es') _lang = stored
  }
}
init()

/** Get the current language */
export function currentLang(): Lang {
  return _lang
}

/** Set the language and notify listeners */
export function setLang(lang: Lang) {
  _lang = lang
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, lang)
  }
  window.dispatchEvent(new CustomEvent('attestto-lang-change', { detail: { lang } }))
}

/** Translate a key */
export function t(key: string): string {
  return translations[_lang][key] ?? translations.en[key] ?? key
}
